import React from "react";
import { useNavigate } from "react-router-dom";
import { CardSkeleton } from "../../../components/skeleton/ListSkeleton";
import { OverviewKpiKey, getOverview } from "../analyticsApi";
import { formatNumber, formatPct, formatRs, higherIsBetterFor, longDate, seriesIsEmpty } from "../analyticsUtils";
import { useAnalyticsPeriod } from "../useAnalyticsPeriod";
import { useAnalyticsFetch } from "../useAnalyticsFetch";
import PeriodPicker from "../components/PeriodPicker";
import KpiCard from "../components/KpiCard";
import HealthScoreCard from "../components/HealthScoreCard";
import { ActiveAreaCard, InactiveAreaCard } from "../components/AreaCard";
import AlertsStrip, { ANALYTICS_BASE } from "../components/AlertsStrip";
import EmptyState from "../components/EmptyState";
import { PeriodBarChart } from "../components/charts";
import { CARD_PAD, Caption, ChartCard, LINK_BTN } from "../components/ui";

const KPIS: { key: OverviewKpiKey; label: string; fmt: (v: number | null) => string; spark?: string }[] = [
  { key: "billed", label: "Billed", fmt: formatRs, spark: "billed" },
  { key: "collected", label: "Collected", fmt: formatRs, spark: "collected" },
  { key: "collectionRate", label: "Collection rate", fmt: formatPct },
  { key: "newPatients", label: "New patients", fmt: formatNumber, spark: "newPatients" },
  { key: "appointments", label: "Appointments", fmt: formatNumber, spark: "appointments" },
  { key: "noShowRate", label: "No-show rate", fmt: formatPct },
  { key: "planCompletionRate", label: "Plan completion", fmt: formatPct },
  { key: "averageBill", label: "Average bill", fmt: formatRs },
];

export default function AnalyticsOverview() {
  const period = useAnalyticsPeriod();
  const navigate = useNavigate();
  const { data, loading } = useAnalyticsFetch(() => getOverview({ from: period.from, to: period.to }), [period.from, period.to]);

  const sparkOf = (key?: string) => (key && data ? data.series.map((p) => (typeof p[key] === "number" ? (p[key] as number) : null)) : undefined);
  const noData = !loading && data != null && (!data.hasData || seriesIsEmpty(data.series, ["billed", "collected", "appointments", "newPatients"]));

  return (
    <div className="space-y-4 text-slate-800 dark:text-slate-200">
      <PeriodPicker period={period} />

      {loading || !data ? (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <CardSkeleton cards={3} height="h-48" />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <CardSkeleton cards={8} height="h-28" />
          </div>
        </>
      ) : (
        <>
          <AlertsStrip alerts={data.alerts} />

          {data.health ? (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <HealthScoreCard health={data.health} />
              <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-4">
                {data.health.areas.map((area) => (
                  <ActiveAreaCard key={area.key} area={area} />
                ))}
                {data.health.inactive.map((area) => (
                  <InactiveAreaCard key={area.key} area={area} />
                ))}
              </div>
            </div>
          ) : (
            <div className={CARD_PAD}>
              <p className="text-sm font-semibold">Clinic Health Score</p>
              <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">Not enough data yet — the score appears once the first monthly snapshot is written.</p>
            </div>
          )}

          {noData ? (
            <EmptyState />
          ) : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {KPIS.map((k) => (
                  <KpiCard
                    key={k.key}
                    label={k.label}
                    value={k.fmt(data.kpis[k.key]?.current ?? null)}
                    change={data.kpis[k.key]?.change ?? null}
                    higherIsBetter={higherIsBetterFor(k.key)}
                    sparkline={sparkOf(k.spark)}
                  />
                ))}
              </div>

              <ChartCard title="Billed vs collected" hint={data.period.granularity === "month" ? "Per month" : "Per day"}>
                <PeriodBarChart
                  series={data.series}
                  specs={[
                    { key: "billed", label: "Billed" },
                    { key: "collected", label: "Collected" },
                  ]}
                  format="rs"
                />
              </ChartCard>
            </>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className={CARD_PAD}>
              <div className="flex items-start justify-between gap-2">
                <p className="text-xs font-medium text-slate-500 dark:text-slate-400">Unfinished plans</p>
                <button type="button" className={LINK_BTN} onClick={() => navigate(`${ANALYTICS_BASE}/treatments`)}>
                  Open Treatments →
                </button>
              </div>
              {data.unfinished ? (
                <>
                  <p className="text-2xl font-bold tabular-nums mt-1">{formatRs(data.unfinished.value)}</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                    {formatNumber(data.unfinished.lines)} open lines across {formatNumber(data.unfinished.patients)} patients with no upcoming appointment
                  </p>
                </>
              ) : (
                <p className="text-sm text-slate-400 dark:text-slate-500 mt-2">Not enough data yet</p>
              )}
              {data.health?.asOfDay && <Caption>As of {longDate(data.health.asOfDay)}</Caption>}
            </div>
            <div className={CARD_PAD}>
              <div className="flex items-start justify-between gap-2">
                <p className="text-xs font-medium text-slate-500 dark:text-slate-400">At-risk patients</p>
                <button type="button" className={LINK_BTN} onClick={() => navigate(`${ANALYTICS_BASE}/patients`)}>
                  Open Patients →
                </button>
              </div>
              {data.atRisk ? (
                <div className="flex gap-6 mt-1">
                  <div>
                    <p className="text-2xl font-bold tabular-nums text-amber-600 dark:text-amber-400">{formatNumber(data.atRisk.amber)}</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">6–12 months idle</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold tabular-nums text-rose-600 dark:text-rose-400">{formatNumber(data.atRisk.red)}</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">over 12 months idle</p>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-slate-400 dark:text-slate-500 mt-2">Not enough data yet</p>
              )}
              {data.health?.asOfDay && <Caption>As of {longDate(data.health.asOfDay)}</Caption>}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
